import { useState, useMemo, useRef } from "react";
import { computeSheet, colName, emptyCells } from "@/lib/sheetEngine";
import { BTN_PRIMARY, BTN_GHOST, ICON_BTN } from "@/lib/styles";
import { X, Plus, Minus, Table2 } from "lucide-react";
import { toast } from "sonner";

export function CalcTableModal({ open, onClose, onInsert }) {
  const [cells, setCells] = useState(() => emptyCells(4, 3));
  const [focus, setFocus] = useState(null);
  const inputsRef = useRef({});

  const { display } = useMemo(() => computeSheet(cells), [cells]);
  const nrows = cells.length;
  const ncols = cells[0]?.length || 0;

  if (!open) return null;

  const setCell = (r, c, v) => setCells((prev) => { const n = prev.map((row) => row.slice()); n[r][c] = v; return n; });
  const addRow = () => setCells((p) => [...p, Array.from({ length: ncols }, () => "")]);
  const addCol = () => setCells((p) => p.map((row) => [...row, ""]));
  const delRow = () => { if (nrows > 1) setCells((p) => p.slice(0, -1)); };
  const delCol = () => { if (ncols > 1) setCells((p) => p.map((row) => row.slice(0, -1))); };

  const insert = () => {
    const hasValue = cells.some((row) => row.some((v) => String(v).trim() !== ""));
    if (!hasValue) { toast.info("Escribe algún valor primero"); return; }
    onInsert(cells);
    setCells(emptyCells(4, 3));
    onClose();
    toast.success("Hoja de cálculo insertada en tu nota");
  };

  return (
    <div className="fixed inset-0 z-[75] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="calc-table-modal">
      <div className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-medium text-lg flex items-center gap-2"><Table2 size={20} className="text-emerald-500" /> Tabla con cálculos</h3>
          <button onClick={onClose} className={ICON_BTN} data-testid="calc-table-close"><X size={16} /></button>
        </div>
        <p className="font-body text-sm text-slate-500 dark:text-slate-400 mb-4">Escribe valores y fórmulas (ej: <code className="text-emerald-500">=SUMA(A1:A3)</code>). Se insertará una <b>hoja de cálculo editable</b> dentro de tu nota (podrás seguir editándola en la Vista previa).</p>

        <div className="flex flex-wrap items-center gap-2 mb-3">
          <button onClick={addRow} className={`${BTN_GHOST} text-xs py-1.5`} data-testid="calc-table-add-row"><Plus size={13} /> Fila</button>
          <button onClick={delRow} className={`${BTN_GHOST} text-xs py-1.5`} data-testid="calc-table-del-row"><Minus size={13} /> Fila</button>
          <button onClick={addCol} className={`${BTN_GHOST} text-xs py-1.5`} data-testid="calc-table-add-col"><Plus size={13} /> Columna</button>
          <button onClick={delCol} className={`${BTN_GHOST} text-xs py-1.5`} data-testid="calc-table-del-col"><Minus size={13} /> Columna</button>
        </div>

        <div className="overflow-auto border border-slate-200 dark:border-slate-800 rounded-xl mb-4">
          <table className="border-collapse text-sm">
            <thead>
              <tr>
                <th className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 w-8"></th>
                {Array.from({ length: ncols }, (_, c) => (
                  <th key={c} className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono2 text-[11px] text-slate-500 min-w-[90px] px-2 py-1.5">{colName(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cells.map((row, r) => (
                <tr key={r}>
                  <td className="bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono2 text-[11px] text-slate-500 text-center w-8">{r + 1}</td>
                  {row.map((_, c) => {
                    const key = `${r}:${c}`;
                    const editing = focus === key;
                    const shown = editing ? cells[r][c] : (display[r]?.[c] ?? "");
                    return (
                      <td key={c} className="border border-slate-200 dark:border-slate-800 p-0">
                        <input
                          ref={(el) => { inputsRef.current[key] = el; }}
                          value={shown}
                          onFocus={() => setFocus(key)}
                          onBlur={() => setFocus(null)}
                          onChange={(e) => setCell(r, c, e.target.value)}
                          className="w-full min-w-[90px] px-2 py-1.5 bg-transparent focus:bg-indigo-50 dark:focus:bg-indigo-500/10 focus:outline-none font-mono2 text-[13px]"
                          data-testid={`calc-table-cell-${colName(c)}${r + 1}`}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className={BTN_GHOST} data-testid="calc-table-cancel">Cancelar</button>
          <button onClick={insert} className={BTN_PRIMARY} data-testid="calc-table-insert">Insertar en la nota</button>
        </div>
      </div>
    </div>
  );
}
